/*
  ARPIVIA — Comportement du bloc d'achat de la fiche produit
  ------------------------------------------------------------
  Chargé de façon bloquante depuis blocks/arpivia-pdp-entete.liquid, tout en
  haut de la colonne d'achat. C'est volontaire : la logique installée ici doit
  exister AVANT que le navigateur n'analyse le bouton d'ajout au panier, faute
  de quoi « Ajouter au panier » s'affiche une fraction de seconde avant d'être
  remplacé par « Choisir ma pointure ».

  Sept responsabilités, et rien d'autre :

  1. VERROU DE TAILLE. Tant que l'acheteuse n'a pas choisi une taille
     elle-même, l'ajout au panier est refusé, un message d'erreur est affiché
     et le focus est déplacé sur le premier bouton de taille. Aucune taille
     n'est cochée et aucun identifiant de variante n'est réécrit : la
     résolution de variante reste entièrement celle de Shopify.

     Le rendu initial ne coche déjà aucune taille — voir la boucle des
     valeurs dans snippets/arpivia-variant-main-picker.liquid. Mais au
     changement de coloris, le thème demande au serveur
     `?option_values=<coloris>` : Shopify résout alors une variante complète
     à partir de cette sélection pourtant partielle, `product.selected_variant`
     cesse d'être vide, et le fragment renvoyé recoche la taille de la
     variante retenue. Aucun signal Liquid ne distingue « le coloris vient
     d'être choisi » de « la variante est confirmée » : le décochage est donc
     réappliqué ici après chaque morphing, tant que le verrou est actif.

  2. LIBELLÉS DU CTA. « Choisissez votre taille » avant le choix, puis le
     libellé d'ajout au panier, tous deux réglés depuis l'éditeur. Une
     consigne du même texte est posée sous le sélecteur tant que rien n'est
     choisi : le libellé du bouton disparaît de la vue dès que la page
     défile, la consigne reste au niveau des pastilles de taille, là où le
     geste attendu doit être fait. Elle est distincte du message d'erreur,
     qui n'apparaît qu'après une tentative d'ajout.

  3. BARRE PERSISTANTE — CONTENU. La barre native affiche le titre brut de
     la variante (« 1 Pair White Grey / Xl »). Il est masqué par la feuille
     de style et remplacé par la taille choisie, ou son absence.

  4. FLÈCHES DE GALERIE SUR MOBILE. La classe utilitaire `mobile:hidden` du
     thème est déclarée en `!important` : seule sa suppression fonctionne.

  5. ANCRE DE L'ACCORDÉON ENTRETIEN. La FAQ renvoie vers un accordéon du
     bloc d'achat, qui est un bloc natif sans identifiant. On lui en pose un,
     repéré par son titre — réglable — et non par sa position.

  6. BARRE PERSISTANTE — VISIBILITÉ. Voir le long commentaire plus bas.

  7. GUIDE DES TAILLES EN MODALE. Voir le commentaire plus bas.

  8. ANNONCE DU NUMÉRO DE DIAPOSITIVE. Le compteur « 1 / 7 » du carrousel est
     un simple `<div>` : il change à l'écran sans que rien ne soit annoncé, et
     lu tel quel il donnerait « un barre oblique sept ». Une zone d'état
     invisible, mise à jour en même temps que lui, énonce « Image 1 sur 7 ».
     Elle est posée ici et non dans snippets/slideshow-controls.liquid, qui
     sert aussi les carrousels de l'accueil.

  ------------------------------------------------------------
  ORDONNANCEMENT — RÉÉCRIT LE 2 SEPTEMBRE 2026
  ------------------------------------------------------------
  La version précédente posait un MutationObserver sur `document.documentElement`
  en `subtree: true`, et relançait `apply()` dans une MICROTÂCHE à chaque lot de
  mutations. Pendant l'analyse du document, chaque insertion de nœud — la nôtre,
  celle du thème, celle de Loox, de Meta, de Google, des composants Shopify —
  réveillait donc un `apply()` complet, avec une quinzaine de `querySelector`
  sur tout le document à chaque fois. Lighthouse mesurait 1,48 s de processeur
  mobile pour ce seul fichier.

  Quatre changements, sans rien retirer aux huit responsabilités :

  1. PÉRIMÈTRE. L'observateur ne regarde plus que la SECTION produit, résolue
     depuis `document.currentScript` — le script est écrit à l'intérieur d'elle.
     Tout ce que ce fichier manipule y vit : galerie, sélecteur, boutons, barre
     persistante, compteur. L'en-tête, le pied de page, le tiroir de panier, les
     douze sections éditoriales, l'iframe Loox et les balises marketing sont
     désormais hors champ.

  2. CADENCE. `apply()` est regroupé dans un unique `requestAnimationFrame`,
     partagé avec le recalcul de la barre persistante : au plus une exécution
     par image, quel que soit le nombre de mutations ou d'événements de
     défilement. Tant que le bouton d'achat n'est pas encore analysé, la
     planification reste en microtâche mais ne fait qu'un `querySelector` — il
     faut que le libellé soit corrigé avant la première peinture du bouton,
     sinon le libellé du thème apparaît puis change.

  3. MÉMOIRE. Les nœuds stables sont mémorisés dans `dom` et revalidés par
     `isConnected`. Le cache est vidé quand — et seulement quand — une mutation
     a réellement eu lieu.

  4. PAS DE BOUCLE. `apply()` écrit dans le DOM ; ces écritures produisent des
     mutations qui réveilleraient l'observateur. `observer.takeRecords()` est
     donc appelé juste après chaque `apply()` : les enregistrements que nous
     venons de provoquer sont jetés. `apply()` étant synchrone, rien d'étranger
     ne peut s'être glissé entre sa lecture du DOM et cette purge.

  ÉVÉNEMENTS DU THÈME PLUTÔT QU'OBSERVATION. Le choix d'une pointure passe par
  `change`, le retour d'historique par `pageshow`, l'éditeur par
  `shopify:section:load` — qui réinstalle l'observateur, la section rendue étant
  un nouvel élément. L'observateur ne sert plus qu'au morphing de section
  déclenché par le changement de coloris, pour lequel le thème n'émet aucun
  événement postérieur au remplacement du DOM.
*/
(function () {
  'use strict';

  var SIZE_FIELDSET = '[data-arpivia-option-role="size"]';
  var PICKER = 'variant-picker.arpivia-variant-picker';
  var CARE_ID = 'arpivia-entretien';

  /* Les deux liens « Guide des tailles » de la fiche : celui rendu dans la
     légende du sélecteur, et celui de la bande autonome — éteinte par défaut,
     mais réactivable depuis l'éditeur. */
  var GUIDE_TRIGGER = 'a.arpivia-variant-picker__guide, a.arpivia-pdp-guide__link';
  var GUIDE_DIALOG_ID = 'arpivia-guide-dialog';

  /* Hauteur réservée en bas de fenêtre. Le CTA réel n'est considéré comme
     « utilisable » que s'il est au-dessus de cette zone : sinon la barre
     persistante viendrait se poser exactement dessus. */
  var BAR_ZONE = 100;

  /* Résolue tout de suite : `document.currentScript` n'est plus renseigné une
     fois l'exécution synchrone terminée. */
  var SCRIPT = document.currentScript;

  var chosenSize = null;
  var frameScheduled = false;
  var microScheduled = false;
  var needsApply = false;
  var needsSticky = false;
  var ready = false;
  var observer = null;
  var root = null;

  /* --- POURQUOI LA BARRE N'EST PAS CALCULÉE AVANT `load` --------------
     `updateStickyVisibility()` lit quatre `getBoundingClientRect()`. Chacune
     force le navigateur à recalculer la mise en page de tout le document — sur
     un DOM de 1 800 éléments encore en cours d'analyse, cela se paie très
     cher : deux tâches longues de près de 300 ms restaient attribuées à ce
     fichier après le premier tour d'optimisation, uniquement à cause de ces
     lectures.

     Or la barre n'a rien à décider tant que la page n'a pas bougé : elle est
     rendue masquée (`data-stuck="false"`), et ses quatre conditions dépendent
     toutes de la position de défilement. Le calcul est donc reporté au premier
     des deux événements qui le rendent utile — un défilement, ou `load`. Avant
     cela, la fonction sort immédiatement, y compris quand le composant du
     thème écrit `data-stuck` de son côté. */
  var layoutReady = false;

  /* Appelée à chaque défilement et à chaque redimensionnement, pas seulement
     la première fois : elle lève la garde ET replanifie le calcul. Une version
     qui sortait tôt une fois `layoutReady` posé a figé la barre persistante
     après le tout premier défilement — relevé en test, à 3 000 px de
     défilement la barre restait `data-stuck="false"`.

     Il n'y a pas de dépense inutile : `scheduleSticky()` ne fait que lever un
     drapeau, et `scheduleFrame()` ne planifie rien tant qu'une image est déjà
     en attente. Le coût reste d'un recalcul par image, quel que soit le nombre
     d'événements de défilement. */
  function allowLayoutReads() {
    layoutReady = true;
    scheduleSticky();
  }

  /* --- RACINE OBSERVÉE ------------------------------------------------
     Le script est écrit dans blocks/arpivia-pdp-entete.liquid, en tête de la
     colonne d'achat : au moment où il s'exécute, la balise ouvrante de sa
     section est déjà analysée, donc `closest` la trouve. Les replis servent le
     cas de l'éditeur, où la section est rendue puis injectée. */
  function resolveRoot() {
    if (root && root.isConnected) return root;
    root = null;
    if (SCRIPT && SCRIPT.closest && SCRIPT.isConnected) {
      root = SCRIPT.closest('.shopify-section');
    }
    if (!root) {
      var cfg = document.querySelector('arpivia-pdp-config');
      if (cfg && cfg.closest) root = cfg.closest('.shopify-section');
    }
    if (!root) {
      var buy = document.querySelector('.buy-buttons-block');
      if (buy && buy.closest) root = buy.closest('.shopify-section');
    }
    if (!root) root = document.getElementById('MainContent');
    return root;
  }

  /* --- MÉMOIRE DES NŒUDS STABLES --------------------------------------
     `undefined` = jamais cherché, `null` = cherché et absent. Le second cas
     est mémorisé lui aussi : une fiche sans barre persistante ne doit pas
     relancer un `querySelector` à chaque image. Le cache est vidé par
     `invalidate()`, appelée uniquement quand le DOM a bougé. */
  var dom = Object.create(null);

  function invalidate() {
    dom = Object.create(null);
  }

  function ref(key, selector) {
    var el = dom[key];
    if (el !== undefined && (el === null || el.isConnected)) return el;
    el = document.querySelector(selector);
    dom[key] = el;
    return el;
  }

  function refAll(key, selector) {
    var list = dom[key];
    if (list !== undefined) return list;
    list = document.querySelectorAll(selector);
    dom[key] = list;
    return list;
  }

  function config() {
    var el = ref('config', 'arpivia-pdp-config');
    return {
      chooseSize: (el && el.dataset.ctaChooseSize) || 'Choisissez votre taille',
      sizeHint: (el && el.dataset.sizeHint) || 'Choisissez votre taille',
      addToCart: (el && el.dataset.ctaAddToCart) || 'Ajouter au panier',
      sizePrefix: (el && el.dataset.stickySizePrefix) || 'Taille',
      sizeMissing: (el && el.dataset.stickySizeMissing) || 'Taille à choisir',
      careHeading: (el && el.dataset.careHeading) || '',
      price: (el && el.dataset.ctaPrice) || ''
    };
  }

  /* Le libelle d'ajout au panier peut porter le gabarit `[prix]`, demande par
     la maquette C1-B (« Ajouter au panier — 24,00 EUR »). Le prix vient de
     `data-cta-price`, ecrit en Liquid dans blocks/arpivia-pdp-entete.liquid :
     la section est re-rendue a chaque changement de variante, l'attribut est
     donc toujours celui de la variante affichee. Aucun element du DOM n'est
     interroge — il n'y a pas d'element « du prix » a deviner.

     Sans gabarit dans le libelle, la fonction rend la chaine inchangee : les
     autres fiches produit ne sont pas concernees. Si le prix est absent, le
     gabarit et le tiret qui le precede sont retires plutot que de laisser
     « Ajouter au panier — » se terminer sur un tiret nu. */
  function withPrice(label, price) {
    if (label.indexOf('[prix]') === -1) return label;
    if (price) return label.split('[prix]').join(price);
    return label.replace(/\s*[—–-]?\s*\[prix\]/g, '').trim();
  }

  function picker() {
    return ref('picker', PICKER);
  }

  function mainButton() {
    return ref('mainButton', '.buy-buttons-block .add-to-cart-button');
  }

  function stickyButton() {
    return ref('stickyButton', '.sticky-add-to-cart__button');
  }

  function creerSpan(classe, texte) {
    var el = document.createElement('span');
    el.className = classe;
    el.textContent = texte;
    return el;
  }

  /* LE LIBELLE EST RENDU EN TROIS MORCEAUX, PAS EN UNE CHAINE

     Sous 768 px le cahier demande « Ajouter au panier » et « CHF 24.00 » sur
     deux lignes centrees, sans le tiret ; au-dessus, la meme phrase sur une
     seule ligne, tiret compris. Un unique noeud de texte ne permet ni l'un
     ni l'autre proprement : un `<br>` figerait la coupure pour le desktop,
     et laisser le navigateur couper la ligne placerait le point de rupture
     la ou tombe la largeur — parfois entre « au » et « panier ».

     Trois elements laissent le CSS decider ou se fait la coupure, et
     seulement a partir de la largeur. Le separateur porte `aria-hidden` : un
     tiret cadratin lu a voix haute n'apporte rien.

     LE NOM ACCESSIBLE NE SUIT PAS CE DECOUPAGE
     `aria-label` porte la phrase complete, identique dans les deux mises en
     page — « Ajouter au panier — CHF 24.00 ». Sans lui, le nom calcule a
     partir du contenu perdrait le tiret sur mobile et changerait donc selon
     la largeur de l'ecran, ce qu'aucune technologie d'assistance n'attend. */
  function peindreLibelle(porteur, texte, montant) {
    porteur.classList.add('arpivia-cta-label');

    var elTexte = porteur.querySelector('.arpivia-cta-label__text');
    var elSep = porteur.querySelector('.arpivia-cta-label__sep');
    var elPrix = porteur.querySelector('.arpivia-cta-label__price');

    if (!elTexte || !elSep || !elPrix) {
      porteur.textContent = '';
      elTexte = creerSpan('arpivia-cta-label__text', '');
      elSep = creerSpan('arpivia-cta-label__sep', '—');
      elSep.setAttribute('aria-hidden', 'true');
      elPrix = creerSpan('arpivia-cta-label__price', '');
      porteur.appendChild(elTexte);
      porteur.appendChild(elSep);
      porteur.appendChild(elPrix);
    }

    if (elTexte.textContent !== texte) elTexte.textContent = texte;
    if (elPrix.textContent !== montant) elPrix.textContent = montant;

    /* Le separateur et le prix ne sont pas retires du DOM quand il n'y a pas
       de prix — l'etat « pointure non choisie » revient a chaque changement
       de variante, et recreer trois noeuds a chaque fois ferait clignoter le
       bouton. Un attribut suffit au CSS pour les masquer. */
    var avecPrix = montant ? 'true' : 'false';
    if (porteur.getAttribute('data-has-price') !== avecPrix) {
      porteur.setAttribute('data-has-price', avecPrix);
    }
  }

  /* UN SEUL LIBELLE, DANS TOUS LES ETATS.

     Trois porteurs de texte peuvent coexister dans un bouton d'ajout au
     panier de ce theme : le span de la barre persistante
     (`.add-to-cart-text__content > span`), le noeud de texte brut du bouton
     principal, et le repli `.arpivia-cta-label`. Ecrire dans le premier
     trouve sans neutraliser les autres est ce qui produisait le libelle en
     double releve sur la page publiee.

     La fonction choisit donc un porteur unique, y peint les trois morceaux,
     puis VIDE tous les autres. Elle est idempotente et reparatrice :
     appliquee a un bouton deja dedouble, elle le ramene a un seul libelle.
     Les porteurs concurrents sont vides plutot que supprimes — un noeud de
     texte du theme est recree a chaque rendu de section, et le supprimer
     laisserait un nom accessible vide si l'application suivante echouait. */
  function setButtonLabel(button, gabarit, prix) {
    if (!button) return;

    var complet = withPrice(gabarit, prix);
    var texte;
    var montant = '';

    if (gabarit.indexOf('[prix]') !== -1 && prix) {
      /* Le tiret vit entre les deux morceaux, pas dans le premier : il est
         rendu par son propre element, que le mobile masque. */
      texte = gabarit.split('[prix]')[0].replace(/[\s—–-]+$/, '');
      montant = prix;
    } else {
      texte = withPrice(gabarit, '');
    }

    var slot = button.querySelector('.add-to-cart-text__content > span');
    var porteur = slot;
    if (!porteur) {
      porteur = button.querySelector('.arpivia-cta-label');
      if (!porteur) {
        porteur = document.createElement('span');
        porteur.className = 'arpivia-cta-label';
        /* Avant la rafale de validation : celle-ci est purement decorative et
           doit rester le dernier enfant du bouton. */
        var added = button.querySelector('.add-to-cart__added');
        if (added) button.insertBefore(porteur, added);
        else button.appendChild(porteur);
      }
    }

    peindreLibelle(porteur, texte, montant);

    var concurrents = button.querySelectorAll('.arpivia-cta-label');
    for (var i = 0; i < concurrents.length; i++) {
      if (concurrents[i] !== porteur && concurrents[i].textContent !== '') {
        concurrents[i].textContent = '';
      }
    }
    for (var j = 0; j < button.childNodes.length; j++) {
      var autre = button.childNodes[j];
      if (autre.nodeType !== 3) continue;
      if (autre.textContent.trim() !== '') autre.textContent = '';
    }

    if (button.getAttribute('aria-label') !== complet) {
      button.setAttribute('aria-label', complet);
    }
  }

  /* La consigne est posée juste sous le sélecteur, avant le message
     d'erreur : elle décrit ce qu'il faut faire, l'erreur ce qui a manqué.
     Pas de `role="alert"` — la consigne est là au chargement, l'annoncer
     couperait la lecture de la page sans qu'il se soit rien passé. */
  function hintElement(create) {
    var existing = ref('hint', '.arpivia-pdp-consigne');
    if (existing || !create) return existing;
    var host = picker();
    if (!host || !host.parentNode) return null;
    var el = document.createElement('p');
    el.className = 'arpivia-pdp-consigne';
    el.hidden = true;
    host.parentNode.insertBefore(el, host.nextSibling);
    dom.hint = el;
    return el;
  }

  function updateHint(cfg, gated) {
    var el = hintElement(gated);
    if (!el) return;
    if (gated && el.textContent !== cfg.sizeHint) el.textContent = cfg.sizeHint;
    el.hidden = !gated;
  }

  function errorElement(create) {
    var existing = ref('error', '.arpivia-pdp-error');
    if (existing || !create) return existing;
    var host = picker();
    if (!host || !host.parentNode) return null;
    var el = document.createElement('p');
    el.className = 'arpivia-pdp-error';
    el.setAttribute('role', 'alert');
    el.hidden = true;
    /* Après la consigne quand elle existe, sinon directement après le
       sélecteur : l'ordre de lecture doit suivre l'ordre des faits. */
    var anchor = document.querySelector('.arpivia-pdp-consigne') || host;
    anchor.parentNode.insertBefore(el, anchor.nextSibling);
    dom.error = el;
    return el;
  }

  function showError() {
    var host = picker();
    if (!host) return;
    var el = errorElement(true);
    if (el) {
      el.textContent = host.dataset.arpiviaSizeError || 'Choisissez d’abord votre taille.';
      el.hidden = false;
    }

    var fieldset = host.querySelector(SIZE_FIELDSET);
    if (!fieldset) return;
    fieldset.setAttribute('data-arpivia-size-error', 'true');

    var first = fieldset.querySelector('input[type="radio"]');
    if (first) {
      try {
        first.focus({ preventScroll: true });
      } catch (e) {
        first.focus();
      }
    }
    if (!first || document.activeElement !== first) {
      fieldset.setAttribute('tabindex', '-1');
      fieldset.focus();
    }
    if (typeof fieldset.scrollIntoView === 'function') {
      fieldset.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  function hideError() {
    var el = errorElement(false);
    if (el) el.hidden = true;
    var host = picker();
    var fieldset = host && host.querySelector(SIZE_FIELDSET);
    if (fieldset) fieldset.removeAttribute('data-arpivia-size-error');
  }

  /* Décoche toute taille recochée par un fragment de section. Écrire la
     propriété `checked` — et non l'attribut — n'émet pas d'événement
     `change` et ne déclenche pas l'observateur, qui ne surveille que
     `childList` : aucune boucle possible. `data-current-checked` suit, car
     c'est lui que `variant-picker.js` lit pour mesurer la pastille de
     sélection ; le laisser à `true` sur une case décochée ferait mesurer une
     pastille qui n'existe pas. */
  function uncheckSizes(fieldset) {
    var checked = fieldset.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < checked.length; i++) {
      checked[i].checked = false;
      checked[i].dataset.currentChecked = 'false';
    }
  }

  function updateStickySize(cfg) {
    var info = ref('stickyInfo', '.sticky-add-to-cart__info');
    if (!info) return;
    var el = info.querySelector('.arpivia-sticky-size');
    if (!el) {
      el = document.createElement('p');
      el.className = 'arpivia-sticky-size';
      info.appendChild(el);
    }
    /* Le prefixe et la valeur sont deux elements distincts : sous 768 px,
       la barre ne garde que la valeur — « 39-43 » — parce que la colonne
       centrale n'a pas la place pour « Pointure 39-43 » sans se tronquer en
       « Pointure… », et parce que le bouton juste a cote dit deja de quoi il
       s'agit. Au-dessus, les deux sont affiches.

       La valeur rendue est celle de l'administration, au trait d'union :
       elle doit se lire a l'identique dans la barre et sur les pastilles. */
    var prefixe = el.querySelector('.arpivia-sticky-size__prefix');
    var valeur = el.querySelector('.arpivia-sticky-size__value');
    if (!prefixe || !valeur) {
      el.textContent = '';
      prefixe = document.createElement('span');
      prefixe.className = 'arpivia-sticky-size__prefix';
      valeur = document.createElement('span');
      valeur.className = 'arpivia-sticky-size__value';
      el.appendChild(prefixe);
      el.appendChild(valeur);
    }

    var textePrefixe = chosenSize ? cfg.sizePrefix : '';
    var texteValeur = chosenSize ? chosenSize : cfg.sizeMissing;
    if (prefixe.textContent !== textePrefixe) prefixe.textContent = textePrefixe;
    if (valeur.textContent !== texteValeur) valeur.textContent = texteValeur;
    var choisi = chosenSize ? 'true' : 'false';
    if (el.getAttribute('data-chosen') !== choisi) el.setAttribute('data-chosen', choisi);
  }

  /* La classe visée s'écrit `mobile:hidden`. Utilisée dans un sélecteur CSS,
     son deux-points doit être échappé — et un antislash traversant plusieurs
     couches d'outillage est une source d'erreur silencieuse : mal échappé, le
     sélecteur devient invalide, `querySelectorAll` lève une exception et tout
     `apply()` s'arrête. On sélectionne donc les flèches sans filtrer sur la
     classe, puis on la retire. La retirer d'un élément qui ne la porte pas ne
     fait rien : le résultat est identique, sans antislash. */
  function revealGalleryArrows() {
    var arrows = refAll('arrows', 'media-gallery slideshow-arrows');
    for (var i = 0; i < arrows.length; i++) {
      if (arrows[i].classList.contains('mobile:hidden')) {
        arrows[i].classList.remove('mobile:hidden');
      }
    }
  }

  /* --- 8. ANNONCE DU NUMÉRO DE DIAPOSITIVE ---------------------------
     Le compteur mobile du thème est `<div class="slideshow-controls__counter">
     <span ref="current">1</span><span class="slash">/</span>7</div>`. Il n'est
     ni une zone live, ni lisible à voix haute : une synthèse vocale en tire
     « 1 / 7 ».

     On ne le transforme pas en zone live directement — annoncer « 1/7 » ne
     vaut guère mieux. Un frère invisible reçoit la phrase complète et porte
     `role="status"`, qui implique déjà `aria-live="polite"`. Il est mis à jour
     par un observateur posé sur le compteur : le thème réécrit le contenu du
     `<span ref="current">` à chaque changement de diapositive, sans émettre
     d'événement qu'on pourrait écouter.

     `.visually-hidden` est la classe du thème, déjà définie dans base.css.

     L'observateur surveille `childList` et `characterData` du compteur, pas
     ses attributs : il ne peut pas être réveillé par l'écriture du texte du
     frère, qui vit en dehors de lui. Un compteur déjà équipé est reconnu à
     son attribut et sauté : la fonction ne pose jamais deux observateurs sur
     le même nœud, même rejouée après un morphing. Chaque observateur se
     débranche de lui-même quand son compteur quitte le document. */
  function announceSlides() {
    var counters = refAll('counters', 'media-gallery .slideshow-controls__counter');
    for (var i = 0; i < counters.length; i++) {
      var counter = counters[i];
      if (counter.hasAttribute('data-arpivia-announced')) continue;
      counter.setAttribute('data-arpivia-announced', '');
      counter.setAttribute('aria-hidden', 'true');

      var status = document.createElement('span');
      status.className = 'visually-hidden arpivia-slide-status';
      status.setAttribute('role', 'status');
      if (counter.parentNode) counter.parentNode.insertBefore(status, counter.nextSibling);

      writeSlideStatus(counter, status);

      /* La fermeture capture le couple compteur / zone d'état : plusieurs
         jeux de contrôles coexistent (bureau et mobile) et chacun a le sien. */
      (function (c, st) {
        var mo = new MutationObserver(function () {
          if (!c.isConnected) {
            mo.disconnect();
            return;
          }
          writeSlideStatus(c, st);
        });
        mo.observe(c, { childList: true, subtree: true, characterData: true });
      })(counter, status);
    }
  }

  function writeSlideStatus(counter, status) {
    var current = counter.querySelector('[ref="current"]');
    if (!current) return;
    var parts = counter.textContent.split('/');
    var total = parts.length > 1 ? parts[parts.length - 1].trim() : '';
    var text = 'Image ' + current.textContent.trim();
    if (total) text += ' sur ' + total;
    if (status.textContent !== text) status.textContent = text;
  }

  function tagCareAccordion(cfg) {
    if (!cfg.careHeading) return;
    if (document.getElementById(CARE_ID)) return;

    var buy = ref('buyBlock', '.buy-buttons-block');
    var scope = buy ? buy.closest('.group-block') || document : document;

    var wanted = cfg.careHeading.trim().toLowerCase();
    var rows = scope.querySelectorAll('details');
    for (var i = 0; i < rows.length; i++) {
      var summary = rows[i].querySelector('summary');
      if (!summary) continue;
      if (summary.textContent.trim().toLowerCase() === wanted) {
        rows[i].id = CARE_ID;
        return;
      }
    }
  }

  function openCareAccordion() {
    if (window.location.hash !== '#' + CARE_ID) return;
    var el = document.getElementById(CARE_ID);
    if (!el) return;
    if (!el.open) el.open = true;
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    var summary = el.querySelector('summary');
    if (summary) {
      summary.setAttribute('tabindex', '-1');
      try {
        summary.focus({ preventScroll: true });
      } catch (e) {
        summary.focus();
      }
    }
  }

  /* --- 7. GUIDE DES TAILLES EN MODALE -------------------------------
     Le déclencheur reste un vrai `<a href>` vers /pages/guide-des-tailles.
     Ce fichier ne fait qu'intercepter le clic quand une modale est
     réellement disponible. L'ordre importe : le lien fonctionne d'abord,
     la modale est un enrichissement.

     `guideHost()` ne renvoie l'élément que si `showDialog` est une vraie
     méthode. `dialog.js` est un module : entre l'analyse du HTML et
     l'exécution du module, `<dialog-component>` existe dans le DOM mais
     n'est pas encore « rehaussé » et n'a aucune méthode. Pendant cette
     fenêtre, le clic navigue vers la page — ce qui est le comportement
     attendu, pas une erreur. Le passage déclenché par `load`, plus bas,
     repose les attributs une fois les modules exécutés.

     Ouvrir avec `showModal()` — ce que fait `showDialog()` — donne le piège
     à focus, le masque d'arrière-plan, la fermeture à Échap et le retour du
     focus sur le lien d'origine, tout cela pris en charge par le navigateur.

     Les clics avec touche de modification et les clics du bouton du milieu
     sont laissés au navigateur : « ouvrir dans un nouvel onglet » doit
     continuer de fonctionner sur un lien.

     `aria-haspopup` et `aria-controls` sont posés depuis le script et non
     dans le Liquid : les annoncer côté serveur serait mentir dans tous les
     cas où la modale n'est pas disponible.
  */
  function guideHost() {
    var host = ref('guideHost', 'dialog-component[data-arpivia-guide]');
    if (!host || typeof host.showDialog !== 'function') return null;
    return host;
  }

  function markGuideTriggers() {
    var links = refAll('guideLinks', GUIDE_TRIGGER);
    if (!links.length) return;
    var available = !!guideHost();
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (available) {
        if (link.getAttribute('aria-haspopup') !== 'dialog') {
          link.setAttribute('aria-haspopup', 'dialog');
        }
        if (link.getAttribute('aria-controls') !== GUIDE_DIALOG_ID) {
          link.setAttribute('aria-controls', GUIDE_DIALOG_ID);
        }
      } else if (link.hasAttribute('aria-haspopup')) {
        link.removeAttribute('aria-haspopup');
        link.removeAttribute('aria-controls');
      }
    }
  }

  function handleGuideClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest(GUIDE_TRIGGER)) return;

    var host = guideHost();
    if (!host) return;

    event.preventDefault();
    host.showDialog();
  }

  /* --- 6. VISIBILITÉ DE LA BARRE PERSISTANTE -------------------------
     Le thème ne montre la barre qu'une fois le bouton d'achat dépassé, très
     bas dans la page. Or une part importante des visiteurs ne va pas plus
     loin que la galerie : ceux-là ne voient jamais de bouton d'achat.

     Une seule fonction, appelée au défilement et au redimensionnement, qui
     lit les positions réelles et décide. Quatre conditions, toutes vérifiées
     sur les mêmes coordonnées au même instant — plus aucun état à
     synchroniser :

       1. la galerie a passé la moitié haute de l'écran ;
       2. le vrai CTA n'est pas utilisable, c'est-à-dire qu'il est soit
          encore plus bas que la zone réservée à la barre, soit déjà sorti
          par le haut ;
       3. le footer n'est pas entré dans la fenêtre ;
       4. le CTA final n'est pas entré dans la fenêtre.

     LE SÉLECTEUR DU CTA FINAL A ÉTÉ CHOISI EN MESURANT, PAS EN LISANT LE CODE
     `.c1b-faq-cta`, la classe déclarée par le schéma du bloc de FAQ, n'existe
     nulle part dans le DOM — Shopify n'applique `class` qu'aux SECTIONS, et
     cette FAQ est un BLOC posé dans une section `_blocks`. `#c1b-faq`, l'ancre
     de la section, existe mais mesure 1 147 px : la barre disparaissait dès
     que la PREMIÈRE question entrait dans la fenêtre, soit 2 000 px trop tôt.
     Retenu : le conteneur du CTA final lui-même, visé par ses deux fragments
     de classe. Il fait 229 px et commence 325 px avant le pied de page.

     Sur une fiche sans cette section, `querySelector` renvoie `null` et la
     condition est neutre : les autres produits ne changent pas de
     comportement.

     Le calcul est encadré par requestAnimationFrame : au plus une fois par
     image, quel que soit le nombre d'événements de défilement.
  */
  function updateStickyVisibility() {
    if (!layoutReady) return;
    var bar = ref('bar', '.sticky-add-to-cart__bar');
    var gallery = ref('gallery', 'media-gallery');
    var buyBlock = ref('buyBlock', '.buy-buttons-block');
    if (!bar || !gallery || !buyBlock) return;

    var viewport = window.innerHeight;
    var galleryBottom = gallery.getBoundingClientRect().bottom;
    var cta = buyBlock.getBoundingClientRect();

    var pastGallery = galleryBottom < viewport * 0.5;
    var ctaUsable = cta.top < viewport - BAR_ZONE && cta.bottom > 0;

    var footer = ref('footer', '#shopify-section-footer') || ref('footerFallback', 'footer');
    var footerVisible = !!footer && footer.getBoundingClientRect().top < viewport;

    var finalCta = ref('finalCta', '[class*="c1b-faq"][class*="__cta"]');
    var finalCtaVisible = !!finalCta && finalCta.getBoundingClientRect().top < viewport;

    var show = pastGallery && !ctaUsable && !footerVisible && !finalCtaVisible;
    var next = show ? 'true' : 'false';
    if (bar.dataset.stuck !== next) bar.dataset.stuck = next;
  }

  /* --- QUI A LE DERNIER MOT SUR `data-stuck` -------------------------
     Le composant du thème écrit lui aussi cet attribut, depuis deux
     IntersectionObserver. Leurs entrées sont livrées de façon asynchrone
     et peuvent être fusionnées : après un défilement rapide, le dernier
     rappel reçu décrit parfois une position intermédiaire et non celle
     où la page s'est arrêtée. Quand ce rappel arrive après la dernière
     image de défilement, c'est sa valeur qui reste — observé en revenant
     du milieu de la page vers le haut : la barre restait affichée
     par-dessus le bloc d'achat, à l'endroit précis où elle ne doit
     jamais l'être.

     Recalculer ici à chaque écriture de l'attribut règle le cas sans
     toucher au composant du thème, qui reste libre de proposer sa
     valeur.

     Pas de boucle possible : `updateStickyVisibility` compare avant
     d'écrire. Le thème écrit `true`, on recalcule, on écrit `false`,
     l'observateur se réveille une seconde fois, le calcul redonne
     `false`, plus rien n'est écrit. */
  function watchStickyBar() {
    var bar = ref('bar', '.sticky-add-to-cart__bar');
    if (!bar || bar.hasAttribute('data-arpivia-watched')) return;
    bar.setAttribute('data-arpivia-watched', '');
    var mo = new MutationObserver(function () {
      if (!bar.isConnected) {
        mo.disconnect();
        return;
      }
      updateStickyVisibility();
    });
    mo.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  }

  function apply() {
    revealGalleryArrows();

    var cfg = config();
    tagCareAccordion(cfg);
    markGuideTriggers();
    announceSlides();
    watchStickyBar();
    if (layoutReady) scheduleSticky();

    var host = picker();
    if (!host) return;

    var requiresSize = host.dataset.arpiviaRequireSize === 'true' && host.dataset.arpiviaHasSize === 'true';
    var fieldset = host.querySelector(SIZE_FIELDSET);

    if (requiresSize && !chosenSize && host.dataset.arpiviaSizeUnconfirmed === 'false' && fieldset) {
      var preset = fieldset.querySelector('input:checked');
      if (preset) chosenSize = preset.getAttribute('data-arpivia-size') || preset.value;
    }

    var gated = requiresSize && !chosenSize;

    if (fieldset) {
      if (gated) {
        fieldset.setAttribute('data-arpivia-size-unconfirmed', 'true');
        uncheckSizes(fieldset);
      } else {
        fieldset.removeAttribute('data-arpivia-size-unconfirmed');
      }
    }
    host.dataset.arpiviaSizeUnconfirmed = gated ? 'true' : 'false';

    updateHint(cfg, gated);

    var label = gated ? cfg.chooseSize : cfg.addToCart;
    var main = mainButton();
    var sticky = stickyButton();

    /* Le prix est desormais porte par les DEUX boutons. La barre
       persistante l'affichait dans une colonne separee, a gauche du bouton :
       deux prix pour un seul panier, et une colonne de moins pour la
       pointure, qui se tronquait en « Pointure… ». Le cahier V8 tranche —
       un seul prix visible dans la barre, et c'est celui du bouton. La
       colonne de prix du theme est masquee par assets/arpivia-c1b.css. */
    setButtonLabel(main, label, cfg.price);
    setButtonLabel(sticky, label, cfg.price);

    [main, sticky].forEach(function (button) {
      if (!button) return;
      if (gated) {
        if (button.getAttribute('aria-disabled') !== 'true') {
          button.setAttribute('aria-disabled', 'true');
        }
        if (button.getAttribute('data-arpivia-gated') !== 'true') {
          button.setAttribute('data-arpivia-gated', 'true');
        }
      } else {
        if (button.hasAttribute('aria-disabled')) button.removeAttribute('aria-disabled');
        if (button.hasAttribute('data-arpivia-gated')) button.removeAttribute('data-arpivia-gated');
      }
    });

    updateStickySize(cfg);

    if (!gated) hideError();
  }

  /* --- CADENCE --------------------------------------------------------
     `frame()` est le seul endroit d'où `apply()` et le recalcul de la barre
     partent : une exécution par image au maximum, mutations et défilements
     confondus.

     `takeRecords()` jette les mutations que `apply()` vient elle-même de
     provoquer. Sans cette purge, chaque écriture rappellerait l'observateur,
     qui replanifierait une image, qui réécrirait — une boucle qui ne
     s'arrêterait que parce que les écritures sont comparées avant d'être
     faites, mais qui aurait quand même coûté une image sur deux. Rien
     d'étranger ne peut être jeté au passage : `apply()` est synchrone, donc
     aucune mutation extérieure ne peut se glisser entre sa lecture du DOM et
     cette purge. */
  function frame() {
    frameScheduled = false;
    var runApply = needsApply;
    var runSticky = needsSticky;
    needsApply = false;
    needsSticky = false;

    if (runApply) {
      apply();
      if (observer) observer.takeRecords();
    } else if (runSticky) {
      updateStickyVisibility();
    }
  }

  /* Un passage complet, tout de suite. Reserve aux chemins declenches par le
     visiteur ou par le theme, ou attendre une image serait au mieux inutile et
     au pire faux — voir scheduleFrame(). */
  function applyNow() {
    needsApply = false;
    apply();
    if (observer) observer.takeRecords();
  }

  function scheduleFrame() {
    if (frameScheduled) return;
    frameScheduled = true;
    window.requestAnimationFrame(frame);
    /* --- POURQUOI UN SECOND DECLENCHEUR ---------------------------------
       `requestAnimationFrame` ne se declenche pas tant que le document est
       cache : onglet en arriere-plan, fenetre reduite, page pre-rendue. Une
       mutation survenue pendant ce temps resterait donc en attente, et la
       fiche se retrouverait dans un etat perime au retour du visiteur —
       constate en test : le verrou de pointure ne se levait pas.

       `frame()` consomme ses drapeaux des sa premiere ligne : la seconde
       invocation, celle qui arrive en trop, ne fait rien. */
    window.setTimeout(frame, 100);
  }

  function scheduleSticky() {
    needsSticky = true;
    scheduleFrame();
  }

  function scheduleApply() {
    needsApply = true;
    scheduleFrame();
  }

  /* --- AVANT LA PREMIÈRE PEINTURE -------------------------------------
     Tant que le bouton d'achat n'a pas été analysé, il n'y a rien à faire, et
     la seule chose qui compte est de ne pas rater l'image où il apparaît :
     corrigé une image trop tard, le libellé du thème serait peint puis
     remplacé sous les yeux du visiteur.

     Cette phase reste donc en microtâche — elle s'exécute avant toute
     peinture — mais son travail se réduit à un `querySelector`. Dès que le
     bouton existe, un `apply()` complet est joué et la cadence passe à
     l'image. */
  function scheduleBootstrap() {
    if (microScheduled) return;
    microScheduled = true;
    Promise.resolve().then(function () {
      microScheduled = false;
      if (ready) return;
      if (!document.querySelector('.buy-buttons-block .add-to-cart-button')) return;
      ready = true;
      invalidate();
      applyNow();
    });
  }

  function onMutations() {
    invalidate();
    if (!ready) {
      scheduleBootstrap();
      return;
    }
    scheduleApply();
  }

  /* --- INSTALLATION DE L'OBSERVATEUR ----------------------------------
     Une seule racine, la section produit. Elle est réinstallée à
     `shopify:section:load` : dans l'éditeur, la section rendue est un NOUVEL
     élément, et l'observateur posé sur l'ancien ne verrait plus rien. */
  function observeRoot() {
    var target = resolveRoot();
    if (!target) return;
    if (observer) observer.disconnect();
    else observer = new MutationObserver(onMutations);
    observer.observe(target, { childList: true, subtree: true });
  }

  function isGated() {
    var host = picker();
    return !!host && host.dataset.arpiviaSizeUnconfirmed === 'true';
  }

  function guardClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('.add-to-cart-button, .sticky-add-to-cart__button');
    if (!button) return;
    if (!isGated()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showError();
  }

  function guardSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
    if (!isGated()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showError();
  }

  function handleChange(event) {
    var input = event.target;
    if (!input || !input.getAttribute) return;
    var size = input.getAttribute('data-arpivia-size');
    if (!size) return;
    chosenSize = size;
    hideError();
    applyNow();
  }

  function handleHash() {
    invalidate();
    applyNow();
    openCareAccordion();
  }

  document.addEventListener('click', guardClick, true);
  document.addEventListener('click', handleGuideClick);
  document.addEventListener('submit', guardSubmit, true);
  document.addEventListener('change', handleChange, true);
  window.addEventListener('hashchange', handleHash);
  window.addEventListener('scroll', allowLayoutReads, { passive: true });
  window.addEventListener('resize', allowLayoutReads, { passive: true });

  /* Retour par l'historique : le DOM restauré du bfcache n'émet aucune
     mutation, donc l'observateur ne dirait rien. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    invalidate();
    observeRoot();
    scheduleApply();
  });

  /* Éditeur de thème : la section est re-rendue en entier, donc remplacée. */
  document.addEventListener('shopify:section:load', function () {
    root = null;
    invalidate();
    observeRoot();
    scheduleApply();
  });

  document.addEventListener('shopify:section:unload', function () {
    if (observer) observer.disconnect();
  });

  observeRoot();

  document.addEventListener('DOMContentLoaded', function () {
    ready = true;
    invalidate();
    applyNow();
    openCareAccordion();
  });

  /* Après `load`, les modules du thème sont exécutés : la modale du guide des
     tailles a enfin ses méthodes, et `markGuideTriggers()` peut poser des
     attributs qui ne mentent pas. */
  window.addEventListener('load', function () {
    invalidate();
    scheduleApply();
    allowLayoutReads();
  });

  scheduleBootstrap();
})();
